import { useState } from "react";

import moment from "moment";
import "moment/locale/es";

import mapLeaveIcon from "../../../../Vacations/components/LeaveTypes/constants/leaveIconMapper";
import { VACATION_REQUESTS_STRINGS } from "../constants/strings";

import StatusChip from "./StatusChip";
import { OverlappingDetailUiT } from "../domain/ui/requestDetail.model";

const stripMonthDots = (str) => str.replaceAll(/\.(?=\s|,)/g, "");

const getDateRange = (startDate, endDate) => {
  const format = "D MMM, YYYY";
  const start = moment.utc(startDate).locale("es");
  const end = moment.utc(endDate).locale("es");

  const startFormatted = stripMonthDots(start.format(format));
  const endFormatted = stripMonthDots(end.format(format));

  if (start.year() !== end.year()) {
    return `${startFormatted} - ${endFormatted}`;
  }

  const startWithoutYear = startFormatted.split(",")[0];
  return `${startWithoutYear} - ${endFormatted}`;
};

type OverlapItemPropsT = {
  overlap: OverlappingDetailUiT;
};

const OverlapItem = ({ overlap }: OverlapItemPropsT) => {
  const [avatarError, setAvatarError] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(true);

  const fullNameSplit = overlap.fullName?.trim().split(" ");
  const initials =
    fullNameSplit?.length > 1
      ? `${fullNameSplit[0][0]}${fullNameSplit.at(-1)[0]}`
      : (overlap.fullName?.substring(0, 2).toUpperCase() ?? "—");

  const OverlapTypeIcon = mapLeaveIcon(overlap.typeIcon);

  const handleAvatarLoaded = () => setAvatarLoading(false);

  const handleAvatarError = () => {
    setAvatarError(true);
    setAvatarLoading(false);
  };

  return (
    <div className="flex bg-gray-100 rounded-lg p-2 gap-2">
      {overlap.avatar && !avatarError ? (
        <>
          {avatarLoading && (
            <div
              aria-hidden="true"
              className="w-7 h-7 rounded-full bg-gray-200 animate-pulse shrink-0"
            />
          )}
          <img
            src={overlap.avatar}
            alt={overlap.fullName}
            className={`w-7 h-7 rounded-full object-cover shrink-0 ${avatarLoading ? "hidden" : ""}`}
            onLoad={handleAvatarLoaded}
            onError={handleAvatarError}
          />
        </>
      ) : (
        <div
          role="img"
          aria-label={overlap.fullName}
          className="w-7 h-7 rounded-full bg-primary-PCP001 flex items-center justify-center text-white font-semibold text-lg shrink-0"
        >
          {initials}
        </div>
      )}
      <dl className="flex flex-col flex-1 min-w-0">
        <dt className="sr-only">Nombre</dt>
        <dd title={overlap.fullName} className="truncate">
          {overlap.fullName}
        </dd>
        <dt className="sr-only">Fechas</dt>
        <dd
          title={getDateRange(overlap.startDate, overlap.endDate)}
          className="text-secondaryText truncate"
        >
          {getDateRange(overlap.startDate, overlap.endDate)}
        </dd>
        <dt className="sr-only">Tipo</dt>
        <dd>
          <div
            role="img"
            aria-label={overlap.typeName}
            className="flex bg-info-I005 rounded-lg p-1 gap-1 items-center min-w-0"
          >
            {OverlapTypeIcon && (
              <OverlapTypeIcon aria-hidden="true" size={16} color="#000000" />
            )}
            <span className="truncate">{overlap.typeName}</span>
          </div>
        </dd>
      </dl>
      <StatusChip status={overlap.status} showBackground={false} />
    </div>
  );
};

type OverlappingSectionPropsT = { overlappingAbsences: OverlappingDetailUiT[] };

const OverlappingSection = ({
  overlappingAbsences,
}: OverlappingSectionPropsT) => {
  if (!overlappingAbsences?.length) return null;

  return (
    <div className="flex flex-col gap-1">
      <h3>{VACATION_REQUESTS_STRINGS.OVERLAPPING_SECTION_TITLE}</h3>
      <ul className="flex flex-col gap-1">
        {overlappingAbsences.map((overlap) => (
          <li key={overlap.requestId}>
            <OverlapItem overlap={overlap} />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default OverlappingSection;
